import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { LangTypeEnum } from '~/modules/lang/types/Lang';
import Notification from '~base/ui/Notification';

import IApiClient from '../IApiClient';
import { IAxiosConfig, IAxiosResponse } from './IAxiosInterfaces';

export default class AxiosClient implements IApiClient {
  api: AxiosInstance;

  constructor(config?: AxiosRequestConfig) {
    this.api = axios.create(config);
    this.api.defaults.baseURL = import.meta.env.VITE_APP_API_URL;
    this.api.defaults.headers.common['Content-Type'] = 'application/json';

    if (!import.meta.env.VITE_APP_API_URL) {
      Notification.showError('Api url is not found!');
    }

    this.setInterceptorResponse();

    // Слушаем сообщения от родителя про обновление токена
    window.addEventListener('message', (event) => {
      if (event.data?.type === 'UPDATE_ACCESS_TOKEN' && event.data?.token) {
        this.setAccessToken(event.data.token);
      }
    });
  }

  // API Methods

  get = <T = IAxiosResponse>(config: IAxiosConfig) => {
    return this.api.get<T>(config.url, config.config);
  };

  post = <T = IAxiosResponse>(config: IAxiosConfig) => {
    return this.api.post<T>(config.url, config.data, config.config);
  };

  put = <T = IAxiosResponse>(config: IAxiosConfig) => {
    return this.api.put<T>(config.url, config.data, config.config);
  };

  delete = <T = IAxiosResponse>(config: IAxiosConfig) => {
    return this.api.delete<T>(config.url, config.config);
  };

  // SETTERS

  setAccessToken = (token: string) => {
    this.api.defaults.headers['Authorization'] = `Bearer ${token}`;
  };

  deleteAccessToken = () => {
    this.api.defaults.headers['Authorization'] = '';
  };

  setLanguage = (language: LangTypeEnum) => {
    this.api.defaults.headers['Accept-Language'] = language;
  };

  protected getApiErrors = (error: string | string[] | null) => {
    if (Array.isArray(error)) {
      error.forEach((error) => Notification.showError(error));
    } else if (error) {
      Notification.showError(error);
    } else {
      Notification.showSomethingWrongError();
    }
  };

  // INTERCEPTORS

  private setInterceptorResponse = () => {
    this.api.interceptors.response.use(
      (response: AxiosResponse<IAxiosResponse>) => {
        // Если запрос успешно выполнился но с ошибкой. Например: ошибки валидации
        if (response?.data?.success === false || response?.data?.errorInfo) {
          this.getApiErrors(response.data.errorInfo);

          return Promise.reject(response);
        }

        if (response?.data?.userInfo) {
          Notification.showSuccess(response.data.userInfo);
        }

        return response;
      },
      async (error) => {
        // Отображение ошибки из бэка
        if (error.response?.data?.errorInfo) {
          this.getApiErrors(error.response.data.errorInfo);
        }

        return Promise.reject(error);
      },
    );
  };
}
